import { ReactNode, Suspense, useRef, useState } from 'react';
import * as styles from './SupplierPicker.css';
import { Box, Dialog, DialogContent, Spinner } from '@ff-it/ui';
import type { Supplier } from '../../types';
import type { Activity, ActivityType, EmbeddedCompany, ProductKind } from 'types';
import { FilterContainer, useFilterState, useKindOptions } from './ProductPicker/Filter';
import { ProductPicker } from './ProductPicker';
import { Form } from '@ff-it/form';
import { EmbeddedProduct } from 'modules/supplier/products';
import { SupplierForm } from './SupplierForm';
import { SubmissionErrors } from '@ff-it/form';

type SupplierFormProps = {
  value: Supplier | null;
  onChange: (value: Supplier | null) => Promise<SubmissionErrors | void> | SubmissionErrors | void;
  onRequestClose: () => void;
  required?: boolean;
  partial?: boolean;
};

export type SupplierPickerProps = SupplierFormProps & {
  type: ActivityType;
  kind?: ProductKind | ProductKind[];
  outlet_or_service_category?: number;
};

export function SupplierPicker({
  value,
  onChange,
  type,
  kind,
  onRequestClose,
  required,
  partial,
  outlet_or_service_category,
}: SupplierPickerProps): ReactNode {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useFilterState(type, kind);
  const kindOptions = useKindOptions(kind);
  const [product, setProduct] = useState<EmbeddedProduct | null>(value?.product || null);

  const handleSubmit = (values: { activity: Activity; provider: EmbeddedCompany }) => {
    // if product present hast to complete
    if (product) {
      onChange({ product, ...values });
    } else {
      // @ts-ignore
      onChange(partial ? { product, ...values } : null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onRequestClose}>
      <DialogContent testId="supplier-picker" className={styles.dialog} initialFocus={searchInputRef} p="4">
        <Form
          onSubmit={handleSubmit}
          className={styles.container}
          initialValues={{
            activity: value?.activity || null,
            provider: value?.provider || null,
          }}
        >
          <FilterContainer
            filter={filter}
            setFilter={setFilter}
            kindOptions={kindOptions}
            searchInputRef={searchInputRef}
          />
          <Suspense
            fallback={
              <Box display="flex" flexGrow={1} alignItems="center" justifyContent="center" p="3">
                <Spinner />
              </Box>
            }
          >
            <div className={styles.columns}>
              <ProductPicker
                filter={filter}
                product={product}
                selectProduct={setProduct}
                outlet_or_service_category={outlet_or_service_category}
              />
              <SupplierForm
                product={product}
                providerId={filter.provider?.id}
                setProduct={setProduct}
                required={required}
                partial={partial}
              />
            </div>
          </Suspense>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
